import React, { useState } from 'react';
import FilterCheckboxList from '../filters/FilterCheckboxList';
import FilterSection from '../filters/FilterSection';
import { FilterOption, getSelectionSummary } from '../filters/selection';

type SectionKey =
  | 'viewMode'
  | 'contributingOrganisations'
  | 'projects'
  | 'locations'
  | 'monitoringGroups'
  | 'monitoringLocations'
  | 'matauranga'
  | 'monitoring'
  | 'calendarView';

const DEFAULT_EXPANDED: Record<SectionKey, boolean> = {
  viewMode: false,
  contributingOrganisations: false,
  projects: false,
  locations: false,
  monitoringGroups: false,
  monitoringLocations: false,
  matauranga: false,
  monitoring: false,
  calendarView: true,
};

export interface TuhonoFilterPanelProps {
  viewMode: string;
  viewModeOptions: FilterOption[];
  contributingOrganisationsOptions: FilterOption[];
  selectedContributingOrganisations: string[];
  contributingProjectOptions: FilterOption[];
  selectedProjects: string[];
  locationOptions: FilterOption[];
  selectedLocations: string[];
  monitoringGroupOptions: FilterOption[];
  selectedMonitoringGroups: string[];
  monitoringLocationOptions: FilterOption[];
  selectedMonitoringLocations: string[];
  mataurangaOptions: FilterOption[];
  selectedMatauranga: string[];
  monitoringOptions: FilterOption[];
  selectedMonitoring: string[];
  calendarType: string;
  calendarTypeOptions: FilterOption[];
  calendarYears: number[];
  calendarYear: number;
  calendarMonths: string[];
  calendarMonth: number;
  calendarWeeks: number[];
  calendarWeek: number;
  calendarDays: number[];
  calendarDay: number;
  onViewModeChange: (mode: string) => void;
  onContributingOrganisationsChange: (selected: string[]) => void;
  onProjectsChange: (selected: string[]) => void;
  onLocationsChange: (selected: string[]) => void;
  onMonitoringGroupsChange: (selected: string[]) => void;
  onMonitoringLocationsChange: (selected: string[]) => void;
  onMataurangaChange: (selected: string[]) => void;
  onMonitoringChange: (selected: string[]) => void;
  onCalendarTypeChange: (type: string) => void;
  onCalendarYearChange: (year: number) => void;
  onCalendarMonthChange: (month: number) => void;
  onCalendarWeekChange: (week: number) => void;
  onCalendarDayChange: (day: number) => void;
}

const TuhonoFilterPanel: React.FC<TuhonoFilterPanelProps> = ({
  viewMode,
  viewModeOptions,
  contributingOrganisationsOptions,
  selectedContributingOrganisations,
  contributingProjectOptions,
  selectedProjects,
  locationOptions,
  selectedLocations,
  monitoringGroupOptions,
  selectedMonitoringGroups,
  monitoringLocationOptions,
  selectedMonitoringLocations,
  mataurangaOptions,
  selectedMatauranga,
  monitoringOptions,
  selectedMonitoring,
  calendarType,
  calendarTypeOptions,
  calendarYears,
  calendarYear,
  calendarMonths,
  calendarMonth,
  calendarWeeks,
  calendarWeek,
  calendarDays,
  calendarDay,
  onViewModeChange,
  onContributingOrganisationsChange,
  onProjectsChange,
  onLocationsChange,
  onMonitoringGroupsChange,
  onMonitoringLocationsChange,
  onMataurangaChange,
  onMonitoringChange,
  onCalendarTypeChange,
  onCalendarYearChange,
  onCalendarMonthChange,
  onCalendarWeekChange,
  onCalendarDayChange,
}) => {
  const [expanded, setExpanded] = useState<Record<SectionKey, boolean>>({ ...DEFAULT_EXPANDED });

  const toggle = (section: SectionKey) => {
    setExpanded((current) => ({ ...current, [section]: !current[section] }));
  };

  const currentViewMode = viewModeOptions.find((option) => option.key === viewMode)?.value || '';
  const calendarTypeLabel = calendarTypeOptions.find((option) => option.key === calendarType)?.value || calendarType;
  const monthLabel = calendarMonths[calendarMonth] || '';
  const calendarSummary =
    calendarType === 'day'
      ? `${calendarTypeLabel} · ${calendarDay} ${monthLabel} ${calendarYear}`
      : calendarType === 'week'
      ? `${calendarTypeLabel} ${calendarWeek} · ${monthLabel} ${calendarYear}`
      : `${calendarTypeLabel} · ${monthLabel} ${calendarYear}`;

  return (
    <div className="stream-filter-panel">
      <FilterSection
        id="tuhono-view-mode"
        title="View Mode"
        icon="eye"
        expanded={expanded.viewMode}
        onToggle={() => toggle('viewMode')}
        summary={currentViewMode}
      >
        <div className="sf-radio-group" role="radiogroup" aria-label="View mode">
          {viewModeOptions.map((option) => {
            const selected = option.key === viewMode;
            return (
              <label className={`sf-radio ${selected ? 'is-selected' : ''}`} key={option.key}>
                <input
                  type="radio"
                  name="tuhono-view-mode"
                  checked={selected}
                  onChange={() => {
                    if (!selected) onViewModeChange(option.key);
                  }}
                />
                <span className="sf-radio__dot" aria-hidden="true" />
                <span>{option.value}</span>
              </label>
            );
          })}
        </div>
      </FilterSection>

      <FilterSection
        id="tuhono-contributing-organisations"
        title="Contributing Organisation"
        icon="organisation"
        expanded={expanded.contributingOrganisations}
        onToggle={() => toggle('contributingOrganisations')}
        summary={getSelectionSummary(selectedContributingOrganisations, contributingOrganisationsOptions)}
      >
        <FilterCheckboxList
          options={contributingOrganisationsOptions}
          selected={selectedContributingOrganisations}
          onChange={onContributingOrganisationsChange}
          ariaLabel="Contributing organisations"
          emptyText="No contributing organisations available"
        />
      </FilterSection>

      <FilterSection
        id="tuhono-projects"
        title="Project"
        icon="project"
        expanded={expanded.projects}
        onToggle={() => toggle('projects')}
        summary={getSelectionSummary(selectedProjects, contributingProjectOptions)}
      >
        <FilterCheckboxList
          options={contributingProjectOptions}
          selected={selectedProjects}
          onChange={onProjectsChange}
          ariaLabel="Projects"
          emptyText="No projects available for this site"
        />
      </FilterSection>

      <FilterSection
        id="tuhono-locations"
        title="Observation Location"
        icon="location"
        expanded={expanded.locations}
        onToggle={() => toggle('locations')}
        summary={getSelectionSummary(selectedLocations, locationOptions)}
      >
        <FilterCheckboxList
          options={locationOptions}
          selected={selectedLocations}
          onChange={onLocationsChange}
          ariaLabel="Observation locations"
          emptyText="No observation locations available"
        />
      </FilterSection>

      <FilterSection
        id="tuhono-monitoring-groups"
        title="Monitoring Group"
        icon="layers"
        expanded={expanded.monitoringGroups}
        onToggle={() => toggle('monitoringGroups')}
        summary={getSelectionSummary(selectedMonitoringGroups, monitoringGroupOptions)}
      >
        <FilterCheckboxList
          options={monitoringGroupOptions}
          selected={selectedMonitoringGroups}
          onChange={onMonitoringGroupsChange}
          ariaLabel="Monitoring groups"
          emptyText="No monitoring groups available"
        />
      </FilterSection>

      <FilterSection
        id="tuhono-monitoring-locations"
        title="Monitoring Location"
        icon="station"
        expanded={expanded.monitoringLocations}
        onToggle={() => toggle('monitoringLocations')}
        summary={getSelectionSummary(selectedMonitoringLocations, monitoringLocationOptions)}
      >
        <FilterCheckboxList
          options={monitoringLocationOptions}
          selected={selectedMonitoringLocations}
          onChange={onMonitoringLocationsChange}
          ariaLabel="Monitoring locations"
          emptyText="No monitoring locations available"
        />
      </FilterSection>

      <FilterSection
        id="tuhono-matauranga"
        title="Mātauranga"
        icon="matauranga"
        expanded={expanded.matauranga}
        onToggle={() => toggle('matauranga')}
        summary={getSelectionSummary(selectedMatauranga, mataurangaOptions)}
      >
        <FilterCheckboxList
          options={mataurangaOptions}
          selected={selectedMatauranga}
          onChange={onMataurangaChange}
          ariaLabel="Mātauranga domains"
          emptyText="No mātauranga domains available"
        />
      </FilterSection>

      <FilterSection
        id="tuhono-monitoring"
        title="Monitoring"
        icon="monitoring"
        expanded={expanded.monitoring}
        onToggle={() => toggle('monitoring')}
        summary={getSelectionSummary(selectedMonitoring, monitoringOptions)}
      >
        <FilterCheckboxList
          options={monitoringOptions}
          selected={selectedMonitoring}
          onChange={onMonitoringChange}
          ariaLabel="Monitoring domains"
          emptyText="No monitoring domains available"
        />
      </FilterSection>

      <FilterSection
        id="tuhono-calendar-view"
        title="Calendar View"
        icon="calendar"
        expanded={expanded.calendarView}
        onToggle={() => toggle('calendarView')}
        summary={calendarSummary}
      >
        <div className="sf-field">
          <label className="sf-field-label" htmlFor="tuhono-calendar-type">
            View
          </label>
          <select
            id="tuhono-calendar-type"
            className="sf-select"
            value={calendarType}
            onChange={(event) => onCalendarTypeChange(event.target.value)}
          >
            {calendarTypeOptions.map((option) => (
              <option key={option.key} value={option.key}>
                {option.value}
              </option>
            ))}
          </select>
        </div>

        <div className="sf-field-grid">
          <div className="sf-field">
            <label className="sf-field-label" htmlFor="tuhono-calendar-year">
              Year
            </label>
            <select
              id="tuhono-calendar-year"
              className="sf-select"
              value={calendarYear}
              onChange={(event) => onCalendarYearChange(Number(event.target.value))}
            >
              {calendarYears.map((year) => (
                <option key={year} value={year}>
                  {year}
                </option>
              ))}
            </select>
          </div>

          <div className="sf-field">
            <label className="sf-field-label" htmlFor="tuhono-calendar-month">
              Month
            </label>
            <select
              id="tuhono-calendar-month"
              className="sf-select"
              value={calendarMonth}
              onChange={(event) => onCalendarMonthChange(Number(event.target.value))}
            >
              {calendarMonths.map((month, index) => (
                <option key={month} value={index}>
                  {month}
                </option>
              ))}
            </select>
          </div>
        </div>

        {calendarType === 'week' ? (
          <div className="sf-field">
            <label className="sf-field-label" htmlFor="tuhono-calendar-week">
              Week
            </label>
            <select
              id="tuhono-calendar-week"
              className="sf-select"
              value={calendarWeek}
              onChange={(event) => onCalendarWeekChange(Number(event.target.value))}
            >
              {calendarWeeks.map((week) => (
                <option key={week} value={week}>
                  {week}
                </option>
              ))}
            </select>
          </div>
        ) : null}

        {calendarType === 'day' ? (
          <div className="sf-field">
            <label className="sf-field-label" htmlFor="tuhono-calendar-day">
              Day
            </label>
            <select
              id="tuhono-calendar-day"
              className="sf-select"
              value={calendarDay}
              onChange={(event) => onCalendarDayChange(Number(event.target.value))}
            >
              {calendarDays.map((day) => (
                <option key={day} value={day}>
                  {day}
                </option>
              ))}
            </select>
          </div>
        ) : null}
      </FilterSection>
    </div>
  );
};

export default TuhonoFilterPanel;
